import { useEffect, useState } from 'react';

import { BACK_TO_TOP } from '../../i18n/el';

/**
 * Α6: **επιστροφή στην κορυφή**, σε σελίδες που έχουν μήκος.
 *
 * Τρεις αποφάσεις, και καμία δεν είναι αισθητική:
 *
 * 1. **Είναι κουμπί, όχι σύνδεσμος σε άγκυρα.** Ο σύνδεσμος `#top` γράφει
 *    καταχώρηση στο ιστορικό, οπότε το «πίσω» του περιηγητή θα σήμαινε «κατέβα
 *    πάλι» αντί «φύγε από τη σελίδα» — η ίδια αντίφαση που έλυσε η **D15** για
 *    τον μόνιμο σύνδεσμο, και δεν χρειάζεται να ξαναλυθεί.
 *
 * 2. **Δεν σκεπάζει ενέργεια.** Κάθεται κάτω δεξιά με απόσταση από τις άκρες,
 *    και **δεν** αποδίδεται καθόλου όσο η σελίδα δεν έχει κυλήσει: ένα κουμπί
 *    που αιωρείται πάνω από κάρτες χωρίς λόγο είναι εμπόδιο. Το κατώφλι είναι
 *    ένα ολόκληρο ύψος παραθύρου — κάτω από αυτό, η κορυφή είναι ήδη κοντά.
 *
 * 3. **Είναι προσβάσιμο.** Πραγματικό `<button>` με ελληνικό `aria-label`, μέσα
 *    στη φυσική σειρά εστίασης· η κύλιση σέβεται το `prefers-reduced-motion`,
 *    γιατί ομαλή κύλιση ολόκληρης σελίδας είναι ακριβώς η κίνηση που ενοχλεί
 *    όποιον τη ζητά μειωμένη. Μετά την κύλιση η **εστίαση μετακινείται στην
 *    αρχή του κυρίως περιεχομένου**: χωρίς αυτό, ο χρήστης πληκτρολογίου
 *    βλέπει την κορυφή αλλά το `Tab` συνεχίζει από το κάτω μέρος.
 */
export default function BackToTop({ threshold = null }) {
    const [isVisible, setVisible] = useState(false);

    useEffect(() => {
        const limit = threshold ?? window.innerHeight;

        const check = () => setVisible(window.scrollY > limit);
        check();

        window.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);

        return () => {
            window.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
        };
    }, [threshold]);

    if (!isVisible) return null;

    const goTop = () => {
        const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });

        // Η εστίαση ακολουθεί την όραση. Το `main` δεν είναι εστιάσιμο από μόνο
        // του, οπότε παίρνει προσωρινό `tabIndex` — η εναλλακτική, να εστιάσουμε
        // το πρώτο εστιάσιμο στοιχείο, θα εξαρτιόταν από τη διάταξη κάθε σελίδας.
        const main = document.querySelector('main');
        if (!main) return;

        main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
        main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
    };

    return (
        <button
            type="button"
            onClick={goTop}
            aria-label={BACK_TO_TOP.label}
            title={BACK_TO_TOP.label}
            className="fixed right-4 bottom-4 z-30 flex size-11 items-center justify-center rounded-full border border-border bg-surface text-accent shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus sm:right-6 sm:bottom-6"
        >
            <i className="fa-solid fa-arrow-up" aria-hidden="true" />
        </button>
    );
}
